/**
 * Utilidades para manejo de subdominios en arquitectura multi-tenant.
 *
 * Flujo de autenticación cross-subdomain:
 *   1. Usuario inicia sesión en app.sicecom.com
 *   2. Backend devuelve JWT con empresa_id y company.ruc
 *   3. Frontend redirige a {ruc}.sicecom.com/#t=TOKEN&r=REFRESH
 *   4. En el subdominio de empresa, main.js lee los tokens del hash y los persiste
 *   5. A partir de ahí, todas las requests llevan el JWT con empresa_id correcto
 *   (el empresa_id viene del JWT, no del subdominio)
 */

const DOMINIO_BASE      = process.env.VUE_APP_DOMINIO_BASE      || 'sicecom.com'
const SUBDOMINIO_LOGIN  = process.env.VUE_APP_SUBDOMINIO_LOGIN  || 'app'

/**
 * Devuelve el subdominio del hostname actual.
 * En localhost devuelve 'localhost' (no se aliasa al subdominio de login).
 */
export function obtenerSubdominio () {
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return 'localhost'
  const partes = host.split('.')
  return partes.length >= 3 ? partes[0] : ''
}

/**
 * True si estamos en el subdominio de login global (app.sicecom.com) o en localhost.
 */
export function esSubdominioLogin () {
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return true
  return obtenerSubdominio() === SUBDOMINIO_LOGIN
}

/**
 * Obtiene el empresa_id inferido desde el subdominio actual.
 * Retorna null si estamos en localhost, en el subdominio de login, o si el subdominio
 * no es un entero positivo (ej: 3.sicecom.com → empresa_id = 3).
 */
export function obtenerEmpresaIdDesdeSubdominio () {
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return null
  if (esSubdominioLogin()) return null
  const sub = obtenerSubdominio()
  const id = parseInt(sub, 10)
  return (Number.isInteger(id) && id > 0) ? id : null
}

/**
 * Construye la URL de redirección al subdominio de la empresa usando el RUC.
 * Los tokens se pasan codificados en el hash para el handoff cross-subdomain.
 *
 * Devuelve null (sin redirigir) cuando:
 *   - Estamos en localhost (independientemente del NODE_ENV / VUE_APP_ENV)
 *   - No hay ruc o token disponible
 *
 * @param {string} ruc          RUC de la empresa (p.ej. '0302214051001')
 * @param {string} tokenAcceso
 * @param {string|null} tokenRefresco
 * @param {string} rutaDestino  ruta interna de la SPA (ej: '/')
 * @returns {string|null}
 */
export function construirUrlEmpresa (ruc, tokenAcceso, tokenRefresco, rutaDestino = '/') {
  // Nunca redirigir fuera de localhost, sin importar cómo se haya compilado
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return null
  if (!ruc || !tokenAcceso) return null

  const proto  = window.location.protocol
  const params = new URLSearchParams()
  params.set('t', tokenAcceso)
  if (tokenRefresco) params.set('r', tokenRefresco)

  return `${proto}//${ruc}.${DOMINIO_BASE}${rutaDestino}#${params.toString()}`
}

/**
 * Lee los tokens del hash de la URL al aterrizar en el subdominio de empresa.
 * Formato esperado: #t=ACCESS_TOKEN&r=REFRESH_TOKEN
 *
 * @returns {{ tokenAcceso: string, tokenRefresco: string|null }|null}
 */
export function leerTokensDeUrl () {
  const hash = window.location.hash
  if (!hash || !hash.startsWith('#')) return null
  try {
    const params = new URLSearchParams(hash.substring(1))
    const tokenAcceso = params.get('t')
    if (!tokenAcceso) return null
    return {
      tokenAcceso:    decodeURIComponent(tokenAcceso),
      tokenRefresco:  params.get('r') ? decodeURIComponent(params.get('r')) : null
    }
  } catch {
    return null
  }
}

/**
 * Elimina los parámetros de token del hash sin recargar la página.
 */
export function limpiarHashUrl () {
  if (window.history?.replaceState) {
    history.replaceState(null, '', window.location.pathname + window.location.search)
  }
}

/**
 * Construye la URL de la página de login (app.sicecom.com/iniciar-sesion)
 * incluyendo la ruta de destino para que después del login se vuelva al lugar correcto.
 *
 * Devuelve null en localhost (no se redirige fuera).
 *
 * @param {string} rutaDestino  Ruta de la SPA a la que regresar tras el login (ej: '/dashboard')
 * @returns {string|null}
 */
export function construirUrlLogin (rutaDestino = '/') {
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return null
  const proto  = window.location.protocol
  const sufijo = (rutaDestino && rutaDestino !== '/')
    ? `?redireccion=${encodeURIComponent(rutaDestino)}`
    : ''
  return `${proto}//${SUBDOMINIO_LOGIN}.${DOMINIO_BASE}/iniciar-sesion${sufijo}`
}

/**
 * Construye la URL de cierre de sesión hacia app.sicecom.com.
 * Incluye ?accion=cerrar-sesion para que app.sicecom.com limpie su propio
 * localStorage antes de montar la app (evita el re-login automático desde
 * la sesión que persiste en el subdominio de login).
 *
 * Devuelve null en localhost.
 *
 * @returns {string|null}
 */
export function construirUrlLogout () {
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return null
  const proto = window.location.protocol
  return `${proto}//${SUBDOMINIO_LOGIN}.${DOMINIO_BASE}/iniciar-sesion?accion=cerrar-sesion`
}

/**
 * Si el subdominio actual parece un RUC (exactamente 13 dígitos) lo devuelve;
 * de lo contrario devuelve null.
 */
export function obtenerRucDesdeSubdominio () {
  const sub = obtenerSubdominio()
  return /^\d{13}$/.test(sub) ? sub : null
}
